import * as Sentry from '@sentry/react';
import { APIProvider } from '@vis.gl/react-google-maps';
import { TriangleAlertIcon } from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorBoundary } from '@/components/ErrorBoundary';
import { getInitialLanguage } from '@/i18n';

declare global {
	interface Window {
		gm_authFailure?: () => void;
	}
}

// Google Maps reports key/auth failures (e.g. InvalidKeyMapError) only through
// the global gm_authFailure callback, so fan it out to every mounted provider.
const authFailureListeners = new Set<() => void>();

// The callback fires once per page load and may fire before a provider mounts,
// so remember it: providers mounted later must start in the failed state too.
let authFailed = false;

if (typeof window !== 'undefined') {
	window.gm_authFailure = () => {
		authFailed = true;
		// A broken key in a deployed environment is silent otherwise — the map just
		// stops working for every user until someone notices.
		Sentry.captureMessage('Google Maps authentication failed (check API key/billing)', 'error');
		authFailureListeners.forEach((listener) => listener());
	};
}

function MapUnavailable() {
	const { t } = useTranslation();
	return (
		<div className="flex h-full w-full items-center justify-center bg-muted/30 p-4">
			<div className="w-full max-w-sm rounded-lg border border-border bg-card p-4 text-center shadow-sm">
				<TriangleAlertIcon className="mx-auto mb-2 size-6 text-muted-foreground" />
				<p className="text-sm font-medium">{t('assets.map.unavailableTitle')}</p>
				<p className="mt-1 text-xs text-muted-foreground">{t('assets.map.unavailableBody')}</p>
			</div>
		</div>
	);
}

export type GoogleMapsProviderProps = {
	children: ReactNode;
};

/**
 * Wraps @vis.gl/react-google-maps APIProvider so that a failing Maps API
 * (invalid key, blocked script, auth failure) degrades to an inline fallback
 * instead of crashing the whole route.
 */
export function GoogleMapsProvider({ children }: GoogleMapsProviderProps) {
	const [failed, setFailed] = useState(authFailed);

	useEffect(() => {
		const onAuthFailure = () => setFailed(true);
		authFailureListeners.add(onAuthFailure);
		return () => {
			authFailureListeners.delete(onAuthFailure);
		};
	}, []);

	// A missing key never loads the API at all, so treat it as a failure up front.
	if (failed || !import.meta.env.VITE_GOOGLE_MAPS_API_KEY) {
		return <MapUnavailable />;
	}

	return (
		<ErrorBoundary fallback={<MapUnavailable />}>
			<APIProvider
				apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}
				// The Maps script loads once and cannot switch language afterwards, so a later language change only takes effect on reload.
				language={getInitialLanguage()}
				onError={(error) => {
					Sentry.captureException(error);
					setFailed(true);
				}}
			>
				{children}
			</APIProvider>
		</ErrorBoundary>
	);
}
